// Activity Timeline Service for Memara
// Tracks user activities, achievements, and provides gamification features

export interface ActivityEvent {
  id: string;
  type:
    | 'memory_created'
    | 'memory_updated'
    | 'memory_deleted'
    | 'memory_listed'
    | 'memory_fetched'
    | 'search_performed'
    | 'space_created'
    | 'achievement_unlocked'
    | 'milestone_reached';
  title: string;
  description: string;
  timestamp: Date;
  metadata: {
    spaceId?: string;
    spaceName?: string;
    memoryId?: string;
    memoryTitle?: string;
    searchQuery?: string;
    achievementId?: string;
    value?: number;
    streak?: number;
  };
  impact: 'high' | 'medium' | 'low';
  category: 'creation' | 'search' | 'organization' | 'achievement';
}

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: 'creation' | 'search' | 'streak' | 'milestone' | 'mastery';
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  requirement: {
    type: string;
    value: number;
    timeframe?: 'daily' | 'weekly' | 'monthly' | 'all_time';
  };
  unlockedAt?: Date;
  progress: number; // 0-100
  isUnlocked: boolean;
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
}

export interface UserStats {
  totalMemories: number;
  totalSearches: number;
  totalSpaces: number;
  currentStreak: number;
  longestStreak: number;
  timeSpentSaving: number; // in minutes
  productivityScore: number;
  level: number;
  experience: number;
  nextLevelExp: number;
  joinedDate: Date;
  lastActiveDate: Date;
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  icon: string;
  value: number;
  isReached: boolean;
  reachedAt?: Date;
  category: 'memories' | 'searches' | 'time_saved' | 'streak' | 'productivity';
}

class ActivityTimelineService {
  private static instance: ActivityTimelineService;
  private activities: ActivityEvent[] = [];
  private achievements: Achievement[] = [];
  private userStats: UserStats = {
    totalMemories: 0,
    totalSearches: 0,
    totalSpaces: 1,
    currentStreak: 0,
    longestStreak: 0,
    timeSpentSaving: 0,
    productivityScore: 0,
    level: 1,
    experience: 0,
    nextLevelExp: 100,
    joinedDate: new Date(),
    lastActiveDate: new Date()
  };
  private milestones: Milestone[] = [];
  /** When activity rows are not persisted, cap streak bumps to once per local day. */
  private lastSyntheticStreakDay: string | null = null;
  private readonly STORAGE_KEY = 'memara_activity_timeline';
  private readonly ACHIEVEMENTS_KEY = 'memara_achievements';
  private readonly STATS_KEY = 'memara_user_stats';
  private readonly MILESTONES_KEY = 'memara_milestones';

  private constructor() {
    this.loadFromStorage();
    this.initializeAchievements();
    this.initializeMilestones();
    this.initializeUserStats();
  }

  public static getInstance(): ActivityTimelineService {
    if (!ActivityTimelineService.instance) {
      ActivityTimelineService.instance = new ActivityTimelineService();
    }
    return ActivityTimelineService.instance;
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.activities = parsed.map((activity: any) => ({
          ...activity,
          timestamp: new Date(activity.timestamp)
        }));
      }

      const storedAchievements = localStorage.getItem(this.ACHIEVEMENTS_KEY);
      if (storedAchievements) {
        const parsed = JSON.parse(storedAchievements);
        this.achievements = parsed.map((achievement: any) => ({
          ...achievement,
          unlockedAt: achievement.unlockedAt ? new Date(achievement.unlockedAt) : undefined
        }));
      }

      const storedStats = localStorage.getItem(this.STATS_KEY);
      if (storedStats) {
        const parsed = JSON.parse(storedStats);
        this.userStats = {
          ...parsed,
          joinedDate: new Date(parsed.joinedDate),
          lastActiveDate: new Date(parsed.lastActiveDate)
        };
      }

      const storedMilestones = localStorage.getItem(this.MILESTONES_KEY);
      if (storedMilestones) {
        const parsed = JSON.parse(storedMilestones);
        this.milestones = parsed.map((milestone: any) => ({
          ...milestone,
          reachedAt: milestone.reachedAt ? new Date(milestone.reachedAt) : undefined
        }));
      }
    } catch (error) {
      console.error('Error loading activity timeline from storage:', error);
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.activities));
      localStorage.setItem(this.ACHIEVEMENTS_KEY, JSON.stringify(this.achievements));
      localStorage.setItem(this.STATS_KEY, JSON.stringify(this.userStats));
      localStorage.setItem(this.MILESTONES_KEY, JSON.stringify(this.milestones));
    } catch (error) {
      console.error('Error saving activity timeline to storage:', error);
    }
  }

  /** Persist stats / achievements / milestones only (server owns the activity list). */
  private saveGamificationOnly(): void {
    try {
      localStorage.setItem(this.ACHIEVEMENTS_KEY, JSON.stringify(this.achievements));
      localStorage.setItem(this.STATS_KEY, JSON.stringify(this.userStats));
      localStorage.setItem(this.MILESTONES_KEY, JSON.stringify(this.milestones));
    } catch (error) {
      console.error('Error saving gamification state to storage:', error);
    }
  }

  private initializeUserStats(): void {
    if (!this.userStats) {
      this.userStats = {
        totalMemories: 0,
        totalSearches: 0,
        totalSpaces: 1, // Default space
        currentStreak: 0,
        longestStreak: 0,
        timeSpentSaving: 0,
        productivityScore: 0,
        level: 1,
        experience: 0,
        nextLevelExp: 100,
        joinedDate: new Date(),
        lastActiveDate: new Date()
      };
    }
  }

  private initializeAchievements(): void {
    if (this.achievements.length === 0) {
      this.achievements = [
        // Creation Achievements
        {
          id: 'first_memory',
          title: 'Memory Keeper',
          description: 'Create your first memory',
          icon: '🧠',
          category: 'creation',
          tier: 'bronze',
          requirement: { type: 'memories_created', value: 1 },
          progress: 0,
          isUnlocked: false,
          rarity: 'common'
        },
        {
          id: 'memory_collector',
          title: 'Memory Collector',
          description: 'Create 10 memories',
          icon: '📚',
          category: 'creation',
          tier: 'silver',
          requirement: { type: 'memories_created', value: 10 },
          progress: 0,
          isUnlocked: false,
          rarity: 'uncommon'
        },
        {
          id: 'memory_master',
          title: 'Memory Master',
          description: 'Create 50 memories',
          icon: '🏆',
          category: 'creation',
          tier: 'gold',
          requirement: { type: 'memories_created', value: 50 },
          progress: 0,
          isUnlocked: false,
          rarity: 'rare'
        },
        {
          id: 'memory_legend',
          title: 'Memory Legend',
          description: 'Create 100 memories',
          icon: '👑',
          category: 'creation',
          tier: 'platinum',
          requirement: { type: 'memories_created', value: 100 },
          progress: 0,
          isUnlocked: false,
          rarity: 'epic'
        },

        // Search Achievements
        {
          id: 'first_search',
          title: 'Knowledge Seeker',
          description: 'Perform your first search',
          icon: '🔍',
          category: 'search',
          tier: 'bronze',
          requirement: { type: 'searches_performed', value: 1 },
          progress: 0,
          isUnlocked: false,
          rarity: 'common'
        },
        {
          id: 'search_explorer',
          title: 'Search Explorer',
          description: 'Perform 25 searches',
          icon: '🗺️',
          category: 'search',
          tier: 'silver',
          requirement: { type: 'searches_performed', value: 25 },
          progress: 0,
          isUnlocked: false,
          rarity: 'uncommon'
        },
        {
          id: 'search_master',
          title: 'Search Master',
          description: 'Perform 100 searches',
          icon: '🎯',
          category: 'search',
          tier: 'gold',
          requirement: { type: 'searches_performed', value: 100 },
          progress: 0,
          isUnlocked: false,
          rarity: 'rare'
        },

        // Streak Achievements
        {
          id: 'streak_starter',
          title: 'Streak Starter',
          description: 'Maintain a 3-day activity streak',
          icon: '🔥',
          category: 'streak',
          tier: 'bronze',
          requirement: { type: 'streak_days', value: 3 },
          progress: 0,
          isUnlocked: false,
          rarity: 'common'
        },
        {
          id: 'streak_keeper',
          title: 'Streak Keeper',
          description: 'Maintain a 7-day activity streak',
          icon: '⚡',
          category: 'streak',
          tier: 'silver',
          requirement: { type: 'streak_days', value: 7 },
          progress: 0,
          isUnlocked: false,
          rarity: 'uncommon'
        },
        {
          id: 'streak_champion',
          title: 'Streak Champion',
          description: 'Maintain a 30-day activity streak',
          icon: '🌟',
          category: 'streak',
          tier: 'gold',
          requirement: { type: 'streak_days', value: 30 },
          progress: 0,
          isUnlocked: false,
          rarity: 'rare'
        },
        {
          id: 'streak_legend',
          title: 'Streak Legend',
          description: 'Maintain a 100-day activity streak',
          icon: '💎',
          category: 'streak',
          tier: 'diamond',
          requirement: { type: 'streak_days', value: 100 },
          progress: 0,
          isUnlocked: false,
          rarity: 'legendary'
        },

        // Productivity Achievements
        {
          id: 'time_saver',
          title: 'Time Saver',
          description: 'Save 1 hour of work time',
          icon: '⏰',
          category: 'mastery',
          tier: 'bronze',
          requirement: { type: 'time_saved_minutes', value: 60 },
          progress: 0,
          isUnlocked: false,
          rarity: 'common'
        },
        {
          id: 'productivity_pro',
          title: 'Productivity Pro',
          description: 'Save 10 hours of work time',
          icon: '⚡',
          category: 'mastery',
          tier: 'gold',
          requirement: { type: 'time_saved_minutes', value: 600 },
          progress: 0,
          isUnlocked: false,
          rarity: 'rare'
        },
        {
          id: 'efficiency_expert',
          title: 'Efficiency Expert',
          description: 'Save 40 hours of work time',
          icon: '🚀',
          category: 'mastery',
          tier: 'platinum',
          requirement: { type: 'time_saved_minutes', value: 2400 },
          progress: 0,
          isUnlocked: false,
          rarity: 'epic'
        }
      ];
    }
  }

  private initializeMilestones(): void {
    if (this.milestones.length === 0) {
      this.milestones = [
        {
          id: 'memories_10',
          title: '10 Memories',
          description: 'Created 10 memories',
          icon: '📝',
          value: 10,
          isReached: false,
          category: 'memories'
        },
        {
          id: 'memories_50',
          title: '50 Memories',
          description: 'Created 50 memories',
          icon: '📚',
          value: 50,
          isReached: false,
          category: 'memories'
        },
        {
          id: 'memories_100',
          title: '100 Memories',
          description: 'Created 100 memories',
          icon: '🏆',
          value: 100,
          isReached: false,
          category: 'memories'
        },
        {
          id: 'searches_50',
          title: '50 Searches',
          description: 'Performed 50 searches',
          icon: '🔍',
          value: 50,
          isReached: false,
          category: 'searches'
        },
        {
          id: 'searches_200',
          title: '200 Searches',
          description: 'Performed 200 searches',
          icon: '🎯',
          value: 200,
          isReached: false,
          category: 'searches'
        },
        {
          id: 'time_saved_5h',
          title: '5 Hours Saved',
          description: 'Saved 5 hours of work time',
          icon: '⏰',
          value: 300, // minutes
          isReached: false,
          category: 'time_saved'
        },
        {
          id: 'time_saved_20h',
          title: '20 Hours Saved',
          description: 'Saved 20 hours of work time',
          icon: '⚡',
          value: 1200, // minutes
          isReached: false,
          category: 'time_saved'
        },
        {
          id: 'streak_week',
          title: 'Week Streak',
          description: 'Maintained activity for 7 days',
          icon: '🔥',
          value: 7,
          isReached: false,
          category: 'streak'
        },
        {
          id: 'streak_month',
          title: 'Month Streak',
          description: 'Maintained activity for 30 days',
          icon: '🌟',
          value: 30,
          isReached: false,
          category: 'streak'
        }
      ];
    }
  }

  // Public Methods
  public trackActivity(
    event: Omit<ActivityEvent, 'id' | 'timestamp'>,
    options?: { recordInLocalTimeline?: boolean }
  ): void {
    const recordInLocalTimeline = options?.recordInLocalTimeline !== false;
    const activity: ActivityEvent = {
      ...event,
      id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    if (recordInLocalTimeline) {
      this.lastSyntheticStreakDay = null;
      this.activities.unshift(activity);

      if (this.activities.length > 500) {
        this.activities = this.activities.slice(0, 500);
      }
    }

    this.updateUserStats(activity);

    this.checkAchievements();
    this.checkMilestones();

    if (recordInLocalTimeline) {
      this.saveToStorage();
    } else {
      this.saveGamificationOnly();
    }
  }

  private updateUserStats(activity: ActivityEvent): void {
    this.userStats.lastActiveDate = new Date();
    
    switch (activity.type) {
      case 'memory_created':
        this.userStats.totalMemories++;
        this.userStats.experience += 10;
        break;
      case 'memory_updated':
        this.userStats.experience += 5;
        break;
      case 'memory_deleted':
        this.userStats.experience += 2;
        break;
      case 'search_performed':
        this.userStats.totalSearches++;
        this.userStats.experience += 3;
        break;
      case 'space_created':
        this.userStats.totalSpaces++;
        this.userStats.experience += 15;
        break;
      case 'achievement_unlocked':
        this.userStats.experience += 25;
        break;
    }

    this.updateStreak(activity);

    this.checkLevelUp();
  }

  private updateStreak(activity?: ActivityEvent): void {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const todayStr = today.toDateString();
    const todayActivities = this.getActivitiesForDate(today);
    const yesterdayActivities = this.getActivitiesForDate(yesterday);
    const touchTodayFromSynthetic =
      !!activity && activity.timestamp.toDateString() === todayStr;

    if (todayActivities.length > 0) {
      if (yesterdayActivities.length > 0 || this.userStats.currentStreak === 0) {
        this.userStats.currentStreak++;
      }
    } else if (touchTodayFromSynthetic) {
      if (this.lastSyntheticStreakDay !== todayStr) {
        if (yesterdayActivities.length > 0 || this.userStats.currentStreak === 0) {
          this.userStats.currentStreak++;
        }
        this.lastSyntheticStreakDay = todayStr;
      }
    } else {
      // Check if we should reset streak (after 24 hours of inactivity)
      const lastActivity = this.activities[0];
      if (lastActivity) {
        const hoursSinceLastActivity = (today.getTime() - lastActivity.timestamp.getTime()) / (1000 * 60 * 60);
        if (hoursSinceLastActivity > 24) {
          this.userStats.currentStreak = 0;
        }
      }
    }
    
    if (this.userStats.currentStreak > this.userStats.longestStreak) {
      this.userStats.longestStreak = this.userStats.currentStreak;
    }
  }

  private getActivitiesForDate(date: Date): ActivityEvent[] {
    const dateStr = date.toDateString();
    return this.activities.filter(activity => 
      activity.timestamp.toDateString() === dateStr
    );
  }

  private checkLevelUp(): void {
    while (this.userStats.experience >= this.userStats.nextLevelExp) {
      this.userStats.experience -= this.userStats.nextLevelExp;
      this.userStats.level++;
      this.userStats.nextLevelExp = Math.floor(this.userStats.nextLevelExp * 1.5);
      
      // Track level up as achievement
      this.trackActivity({
        type: 'milestone_reached',
        title: `Level ${this.userStats.level} Reached!`,
        description: `Congratulations on reaching level ${this.userStats.level}!`,
        impact: 'high',
        category: 'achievement',
        metadata: { value: this.userStats.level }
      });
    }
  }

  private checkAchievements(): void {
    this.achievements.forEach(achievement => {
      if (achievement.isUnlocked) return;
      
      let currentValue = 0;
      
      switch (achievement.requirement.type) {
        case 'memories_created':
          currentValue = this.userStats.totalMemories;
          break;
        case 'searches_performed':
          currentValue = this.userStats.totalSearches;
          break;
        case 'streak_days':
          currentValue = this.userStats.currentStreak;
          break;
        case 'time_saved_minutes':
          currentValue = this.userStats.timeSpentSaving;
          break;
      }
      
      achievement.progress = Math.min(100, (currentValue / achievement.requirement.value) * 100);
      
      if (currentValue >= achievement.requirement.value && !achievement.isUnlocked) {
        achievement.isUnlocked = true;
        achievement.unlockedAt = new Date();
        
        // Track achievement unlock
        this.trackActivity({
          type: 'achievement_unlocked',
          title: `Achievement Unlocked: ${achievement.title}`,
          description: achievement.description,
          impact: 'high',
          category: 'achievement',
          metadata: { achievementId: achievement.id }
        });
      }
    });
  }

  private checkMilestones(): void {
    this.milestones.forEach(milestone => {
      if (milestone.isReached) return;
      
      let currentValue = 0;
      
      switch (milestone.category) {
        case 'memories':
          currentValue = this.userStats.totalMemories;
          break;
        case 'searches':
          currentValue = this.userStats.totalSearches;
          break;
        case 'time_saved':
          currentValue = this.userStats.timeSpentSaving;
          break;
        case 'streak':
          currentValue = this.userStats.currentStreak;
          break;
        case 'productivity':
          currentValue = this.userStats.productivityScore;
          break;
      }
      
      if (currentValue >= milestone.value && !milestone.isReached) {
        milestone.isReached = true;
        milestone.reachedAt = new Date();
        
        // Track milestone as activity
        this.trackActivity({
          type: 'milestone_reached',
          title: `Milestone: ${milestone.title}`,
          description: milestone.description,
          impact: 'high',
          category: 'achievement',
          metadata: { value: milestone.value }
        });
      }
    });
  }

  // Getters
  public getRecentActivities(limit: number = 20): ActivityEvent[] {
    return this.activities.slice(0, limit);
  }

  public getActivitiesByCategory(category: ActivityEvent['category'], limit: number = 10): ActivityEvent[] {
    return this.activities.filter(activity => activity.category === category).slice(0, limit);
  }

  public getActivitiesByDateRange(startDate: Date, endDate: Date): ActivityEvent[] {
    return this.activities.filter(activity => 
      activity.timestamp >= startDate && activity.timestamp <= endDate
    );
  }

  public getUnlockedAchievements(): Achievement[] {
    return this.achievements.filter(achievement => achievement.isUnlocked);
  }

  public getAchievementsByCategory(category: Achievement['category']): Achievement[] {
    return this.achievements.filter(achievement => achievement.category === category);
  }

  public getAchievementProgress(): Achievement[] {
    return this.achievements.sort((a, b) => {
      if (a.isUnlocked && !b.isUnlocked) return -1;
      if (!a.isUnlocked && b.isUnlocked) return 1;
      return b.progress - a.progress;
    });
  }

  public getReachedMilestones(): Milestone[] {
    return this.milestones.filter(milestone => milestone.isReached);
  }

  public getUpcomingMilestones(): Milestone[] {
    return this.milestones.filter(milestone => !milestone.isReached);
  }

  public getUserStats(): UserStats {
    return { ...this.userStats };
  }

  public getActivitySummary(): {
    todayActivities: number;
    weekActivities: number;
    totalActivities: number;
    currentStreak: number;
    unlockedAchievements: number;
    reachedMilestones: number;
  } {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    const todayActivities = this.getActivitiesForDate(today).length;
    const weekActivities = this.getActivitiesByDateRange(weekAgo, today).length;
    
    return {
      todayActivities,
      weekActivities,
      totalActivities: this.activities.length,
      currentStreak: this.userStats.currentStreak,
      unlockedAchievements: this.getUnlockedAchievements().length,
      reachedMilestones: this.getReachedMilestones().length
    };
  }

  // Demo data generation
  public generateDemoData(): void {
    const now = new Date();
    const demoActivities: Omit<ActivityEvent, 'id' | 'timestamp'>[] = [
      {
        type: 'memory_created',
        title: 'Created Memory: AI Research Notes',
        description: 'Added comprehensive notes about transformer architecture',
        impact: 'high',
        category: 'creation',
        metadata: { 
          spaceId: 'space_1', 
          spaceName: 'Research', 
          memoryTitle: 'AI Research Notes' 
        }
      },
      {
        type: 'search_performed',
        title: 'Searched: machine learning best practices',
        description: 'Found 12 relevant memories',
        impact: 'medium',
        category: 'search',
        metadata: { 
          searchQuery: 'machine learning best practices',
          value: 12 
        }
      },
      {
        type: 'memory_updated',
        title: 'Updated Memory: Project Timeline',
        description: 'Added new milestones and deadlines',
        impact: 'medium',
        category: 'creation',
        metadata: { 
          spaceId: 'space_2', 
          spaceName: 'Projects', 
          memoryTitle: 'Project Timeline' 
        }
      },
      {
        type: 'space_created',
        title: 'Created Space: Personal Development',
        description: 'New space for tracking learning goals',
        impact: 'high',
        category: 'organization',
        metadata: { 
          spaceId: 'space_3', 
          spaceName: 'Personal Development' 
        }
      }
    ];

    // Add activities with different timestamps
    demoActivities.forEach((activity, index) => {
      const timestamp = new Date(now.getTime() - (index * 2 * 60 * 60 * 1000)); // 2 hours apart
      this.activities.unshift({
        ...activity,
        id: `demo_${Date.now()}_${index}`,
        timestamp
      });
    });

    // Update stats for demo
    this.userStats.totalMemories += 5;
    this.userStats.totalSearches += 8;
    this.userStats.totalSpaces += 2;
    this.userStats.currentStreak = 5;
    this.userStats.timeSpentSaving += 120; // 2 hours
    this.userStats.experience += 100;
    
    this.checkAchievements();
    this.checkMilestones();
    this.saveToStorage();
  }

  public clearDemoData(): void {
    this.activities = [];
    this.achievements.forEach(achievement => {
      achievement.isUnlocked = false;
      achievement.progress = 0;
      achievement.unlockedAt = undefined;
    });
    this.milestones.forEach(milestone => {
      milestone.isReached = false;
      milestone.reachedAt = undefined;
    });
    this.initializeUserStats();
    this.saveToStorage();
  }
}

export default ActivityTimelineService; 